import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LogOut, Menu, Heart, Bell, CheckCheck, Coins, Gem, Users, Megaphone,
  User, Gift, LifeBuoy, ChevronDown, Eye, EyeOff, Sliders, Compass, Sparkles,
  MessageCircle, BookOpen, X, Phone
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { getNotifications, markNotificationsRead, api, updateProfile } from "@/lib/api";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DiscoveryPreferencesDialog from "@/components/DiscoveryPreferencesDialog";
import VisibilityDialog from "@/components/VisibilityDialog";

const ANNOUNCEMENT_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const SHOWN_ANNOUNCEMENTS_KEY = "haven_shown_announcements";
const MAX_REMEMBERED_ANNOUNCEMENTS = 50;

function loadShownAnnouncements() {
  try {
    const raw = localStorage.getItem(SHOWN_ANNOUNCEMENTS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id) => typeof id === "string" && id));
  } catch {
    return new Set();
  }
}

function persistShownAnnouncements(set) {
  try {
    let arr = Array.from(set);
    if (arr.length > MAX_REMEMBERED_ANNOUNCEMENTS) {
      arr = arr.slice(arr.length - MAX_REMEMBERED_ANNOUNCEMENTS);
    }
    localStorage.setItem(SHOWN_ANNOUNCEMENTS_KEY, JSON.stringify(arr));
  } catch {
  }
}

export default function Navbar() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [profileHidden, setProfileHidden] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const shownAnnouncementsRef = useRef(loadShownAnnouncements());

  const isOnNotifications = location.pathname.startsWith("/notifications");

  const considerAnnouncement = useCallback((notif) => {
    if (!notif || notif.type !== "announcement") return false;

    const id = notif.notification_id;
    if (!id) return false;

    if (shownAnnouncementsRef.current.has(id)) return false;

    const createdAt = notif.created_at ? new Date(notif.created_at).getTime() : 0;
    if (!createdAt || Date.now() - createdAt > ANNOUNCEMENT_MAX_AGE_MS) {
      shownAnnouncementsRef.current.add(id);
      persistShownAnnouncements(shownAnnouncementsRef.current);
      return false;
    }

    if (isOnNotifications) return false;

    shownAnnouncementsRef.current.add(id);
    persistShownAnnouncements(shownAnnouncementsRef.current);
    return true;
  }, [isOnNotifications]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const loadData = async () => {
      try {
        const unreadRes = await api.get("/notifications/unread-count");
        if (!cancelled) setUnreadNotifCount(unreadRes.data.unread || 0);
      } catch {}

      try {
        const onlineRes = await api.get("/stats/online");
        if (!cancelled) setOnlineCount(onlineRes.data.online);
      } catch {}

      try {
        const profileRes = await api.get("/profile");
        if (!cancelled && profileRes.data) {
          setProfileHidden(profileRes.data.profile_hidden || false);
        }
      } catch {}

      try {
        const notifs = await getNotifications();
        if (cancelled) return;
        const list = Array.isArray(notifs) ? notifs : [];

        list.sort(
          (a, b) =>
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime()
        );

        for (const n of list) {
          if (considerAnnouncement(n)) {
            setAnnouncement({ message: n.message });
            break;
          }
        }
      } catch {}
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [user, considerAnnouncement]);

  useEffect(() => {
    if (!user) return;
    if (!supabase) {
      console.warn("[Navbar] Realtime disabled — Supabase env vars missing");
      return;
    }

    const channel = supabase.channel(`user:${user.user_id}`);

    channel
      .on("broadcast", { event: "new_notification" }, (payload) => {
        setUnreadNotifCount((prev) => prev + 1);

        const inner = payload?.payload || {};
        const synthetic = {
          notification_id:
            inner.notification_id ||
            `rt_${inner.type || "x"}_${inner.created_at || Date.now()}_${(inner.message || "").slice(0, 32)}`,
          type: inner.type,
          message: inner.message,
          created_at: inner.created_at || new Date().toISOString(),
        };

        if (considerAnnouncement(synthetic)) {
          setAnnouncement({ message: synthetic.message });
        }
      })
      .subscribe();

    return () => {
      try {
        channel.unsubscribe();
      } catch {}
    };
  }, [user, considerAnnouncement]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMarkAllNotifsRead = async () => {
    try {
      await markNotificationsRead();
      setUnreadNotifCount(0);
      toast.success("All notifications marked as read");
    } catch (err) {
      console.error("Failed to mark read:", err);
    }
  };

  const onLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
    navigate("/");
  };

  const handleVisibilityClose = () => {
    setVisibilityOpen(false);
    api.get("/profile")
      .then((res) => {
        if (res.data) setProfileHidden(res.data.profile_hidden || false);
      })
      .catch(() => {});
  };

  if (!user) return null;

  const isActive = (path) => location.pathname.startsWith(path);

  const tokens = user.tokens ?? 0;
  const diamonds = user.diamonds ?? 0;

  const navItems = [
    { to: "/find", label: "Discover", icon: Compass },
    { to: "/matches", label: "Matches", icon: Users },
    { to: "/whatsapp", label: "WhatsApp", icon: Phone },
    { to: "/stories", label: "Community Chats", icon: MessageCircle },
    { to: "/profile", label: "Profile", icon: User },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-[#FAFAF7]/90 backdrop-blur-md border-b border-[#E7E5E0] flex items-center px-3 md:px-6">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <Sheet>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 rounded-md hover:bg-[#F5F3EE] shrink-0">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] bg-[#FAFAF7] p-0 flex flex-col">
              <SheetHeader className="px-5 py-4 border-b border-[#E7E5E0]">
                <SheetTitle className="font-display text-xl flex items-center gap-2">
                  <Heart className="w-5 h-5" fill="currentColor" /> Haven
                </SheetTitle>
              </SheetHeader>

              <div className="px-4 pt-4 pb-3 border-b border-[#E7E5E0] bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-[#E7E5E0] bg-[#F5F3EE] shrink-0">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <User className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{user.name || "User"}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/shop"
                    className="flex items-center gap-2 px-2.5 py-2 bg-amber-50 border border-amber-100 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <Coins className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-sm font-bold text-gray-900 tabular-nums">{tokens}</span>
                      <span className="text-[10px] text-gray-500 uppercase tracking-wide">tokens</span>
                    </div>
                  </Link>
                  <Link
                    to="/shop"
                    className="flex items-center gap-2 px-2.5 py-2 bg-purple-50 border border-purple-100 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <Gem className="w-4 h-4 text-purple-500 shrink-0" />
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-sm font-bold text-gray-900 tabular-nums">{diamonds}</span>
                      <span className="text-[10px] text-gray-500 uppercase tracking-wide">diamonds</span>
                    </div>
                  </Link>
                </div>
              </div>

              <div className="p-4 flex flex-col gap-1 flex-1 overflow-y-auto">
                {navItems.map((item) => (
                  <SheetClose asChild key={item.to}>
                    <Link
                      to={item.to}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                        isActive(item.to)
                          ? "bg-purple-50 text-purple-700"
                          : "text-[#0F0F10] hover:bg-[#F5F3EE]"
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  </SheetClose>
                ))}

                <SheetClose asChild>
                  <Link
                    to="/earn-tokens"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#0F0F10] hover:bg-[#F5F3EE]"
                  >
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Earn tokens
                  </Link>
                </SheetClose>

                <SheetClose asChild>
                  <Link
                    to="/earn-tokens/share"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#0F0F10] hover:bg-[#F5F3EE]"
                  >
                    <Gift className="w-4 h-4 text-pink-600" />
                    Earn 100 tokens (share)
                  </Link>
                </SheetClose>

                <SheetClose asChild>
                  <Link
                    to="/shop"
                    className="flex items-center justify-center gap-2 mt-2 px-3 py-2.5 rounded-lg text-sm font-semibold bg-[#0F0F10] text-white hover:bg-[#262626]"
                  >
                    <Sparkles className="w-4 h-4" /> Upgrade
                  </Link>
                </SheetClose>

                <div className="h-px bg-[#E7E5E0] my-2" />

                <button
                  onClick={handleMarkAllNotifsRead}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#7C3AED] hover:bg-[#F3E8FF]"
                >
                  <CheckCheck className="w-4 h-4" /> Mark all read
                </button>

                <div className="h-px bg-[#E7E5E0] my-2" />

                <SheetClose asChild>
                  <button
                    onClick={() => setTimeout(() => setVisibilityOpen(true), 300)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#0F0F10] hover:bg-[#F5F3EE] w-full text-left"
                  >
                    {profileHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    <span>Visibility</span>
                    <span className="ml-auto text-xs text-[#6B6B70]">
                      {profileHidden ? "Hidden" : "Visible"}
                    </span>
                  </button>
                </SheetClose>

                <SheetClose asChild>
                  <button
                    onClick={() => setTimeout(() => setPrefsOpen(true), 300)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#0F0F10] hover:bg-[#F5F3EE] w-full text-left"
                  >
                    <Sliders className="w-4 h-4" />
                    <span>Preferences</span>
                  </button>
                </SheetClose>

                <div className="h-px bg-[#E7E5E0] my-2" />

                <button
                  onClick={onLogout}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#C4553F] hover:bg-[#F4E7E1]"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/find" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-[#0F0F10] text-white rounded-md flex items-center justify-center">
              <Heart className="w-4 h-4" fill="currentColor" />
            </div>
            <span className="font-display text-lg font-semibold hidden sm:block">Haven</span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-4 ml-6">
          <button
            onClick={() => setVisibilityOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[#262626] hover:bg-[#F5F3EE] transition-colors"
            title="Visibility"
          >
            {profileHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>Visibility</span>
          </button>
          <button
            onClick={() => setPrefsOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[#262626] hover:bg-[#F5F3EE] transition-colors"
            title="Preferences"
          >
            <Sliders className="w-4 h-4" />
            <span>Preferences</span>
          </button>
        </div>

        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <Link
            to="/shop"
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 bg-white/90 hover:bg-white border border-[#E7E5E0] rounded-full transition-colors shrink-0"
            title={`${tokens} tokens · ${diamonds} diamonds — tap to open shop`}
          >
            <span className="flex items-center gap-1">
              <Coins className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="text-xs font-bold text-gray-900 tabular-nums">{tokens}</span>
              <span className="text-[10px] text-gray-500 hidden sm:inline">tokens</span>
            </span>
            <span className="w-px h-3 bg-gray-300" aria-hidden="true" />
            <span className="flex items-center gap-1">
              <Gem className="w-3.5 h-3.5 text-purple-500 shrink-0" />
              <span className="text-xs font-bold text-gray-900 tabular-nums">{diamonds}</span>
              <span className="text-[10px] text-gray-500 hidden lg:inline">diamonds</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1 text-xs text-gray-500 bg-white/80 rounded-full px-3 py-1 border">
            <Users className="w-3 h-3" />
            <span>{onlineCount} online</span>
          </div>

          <Link to="/notifications" className="relative p-2 rounded-md hover:bg-[#F5F3EE] shrink-0">
            <Bell className="w-5 h-5" />
            {unreadNotifCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center">
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </Link>

          <div className="relative shrink-0" ref={profileMenuRef}>
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-[#F5F3EE]"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden border border-[#E7E5E0] bg-[#F5F3EE]">
                {user.picture ? (
                  <img src={user.picture} alt={user.name || "User"} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
              <ChevronDown className="w-3 h-3 text-gray-500 hidden sm:block" />
            </button>

            {profileMenuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white border border-[#E7E5E0] rounded-xl shadow-lg z-50 py-1">
                <Link to="/profile" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F3EE]">
                  <User className="w-4 h-4" /> Profile
                </Link>
                <Link to="/invite" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F3EE]">
                  <Gift className="w-4 h-4" /> Invite
                </Link>
                <div className="h-px bg-[#E7E5E0] my-1" />
                <Link to="/earn-tokens" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F3EE]">
                  <Sparkles className="w-4 h-4 text-purple-600" /> Earn tokens
                </Link>
                <Link to="/earn-tokens/share" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F3EE]">
                  <Gift className="w-4 h-4 text-pink-600" /> Share for 100 tokens
                </Link>
                <div className="h-px bg-[#E7E5E0] my-1" />
                <Link to="/support" onClick={() => setProfileMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F5F3EE]">
                  <LifeBuoy className="w-4 h-4" /> Support
                </Link>
                <div className="h-px bg-[#E7E5E0] my-1" />
                <button onClick={onLogout} className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-[#C4553F] hover:bg-[#F4E7E1]">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside className="hidden md:flex fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-[#E7E5E0] flex-col p-4 z-30">
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-purple-50 text-purple-700"
                  : "text-[#0F0F10] hover:bg-[#F5F3EE]"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="bg-[#FAFAF7] border border-[#E7E5E0] rounded-xl p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Your Balance</span>
            <Link to="/shop" className="text-xs font-medium text-purple-600 hover:underline">Shop →</Link>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/shop" className="flex flex-col items-center bg-white rounded-lg px-2 py-2 border border-amber-100 hover:border-amber-300 transition-colors">
              <Coins className="w-4 h-4 text-amber-500 mb-0.5" />
              <span className="text-base font-bold text-gray-900 tabular-nums leading-tight">{tokens}</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">tokens</span>
            </Link>
            <Link to="/shop" className="flex flex-col items-center bg-white rounded-lg px-2 py-2 border border-purple-100 hover:border-purple-300 transition-colors">
              <Gem className="w-4 h-4 text-purple-500 mb-0.5" />
              <span className="text-base font-bold text-gray-900 tabular-nums leading-tight">{diamonds}</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">diamonds</span>
            </Link>
          </div>
        </div>

        <Link to="/shop" className="flex items-center justify-center gap-2 w-full bg-[#0F0F10] text-white text-center py-2.5 rounded-lg font-semibold text-sm mb-4 hover:bg-[#262626] transition-colors">
          <Sparkles className="w-4 h-4" /> UPGRADE
        </Link>

        <div className="flex items-center gap-3 border-t border-[#E7E5E0] pt-4">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <User className="w-5 h-5" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Discipline</p>
            <p className="text-sm font-medium truncate">
              {user.name || "User"}
              {user?.age ? `, ${user.age}` : ""}
            </p>
          </div>
        </div>
      </aside>

      <Link
        to="/support"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-white border border-[#E7E5E0] shadow-lg rounded-full px-4 py-2.5 text-sm font-medium text-[#0F0F10] hover:shadow-xl transition-shadow"
      >
        <Phone className="w-4 h-4 text-green-600" />
        LIVE SUPPORT
      </Link>

      <VisibilityDialog open={visibilityOpen} onClose={handleVisibilityClose} />
      <DiscoveryPreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />

      {announcement && (
        <AlertDialog open={!!announcement} onOpenChange={() => setAnnouncement(null)}>
          <AlertDialogContent className="bg-white rounded-2xl shadow-xl border border-[#E7E5E0] max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-[#0F0F10]">
                <Megaphone className="w-5 h-5 text-purple-600" /> Announcement
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#404040] text-sm whitespace-pre-wrap">
                {announcement.message}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setAnnouncement(null)} className="bg-purple-600 text-white hover:bg-purple-700">
                Got it
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}